import React, { useState, useEffect, useRef, useCallback, useId } from "react";
import { Icon } from "../../ui/UIElements/Icon";

/**
 * Custom brand (franchise) selector with infinite scroll in the dropdown list.
 * Replaces a native <select> so scroll position can trigger loading the next page.
 */
const FranchiseSelect = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  placeholderLoading,
  loadingMoreLabel,
  franchises = [],
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  disabledSuffix = "",
  required = false,
  "aria-label": ariaLabel,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const listboxId = useId();

  const selectedFranchise = franchises.find(
    (f) => String(f.id) === String(value),
  );
  const displayLabel = selectedFranchise
    ? `${selectedFranchise.name}${!selectedFranchise.enabled ? ` ${disabledSuffix}` : ""}`
    : "";

  const moreBrandsLoadingText = loadingMoreLabel ?? placeholderLoading ?? "";

  const handleToggleOpen = () => {
    if (disabled) return;
    setIsOpen((o) => !o);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, handleClose]);

  const handleScroll = useCallback(() => {
    if (!listRef.current || isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      fetchNextPage?.();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const handleSelectFranchise = (franchise) => {
    if (!franchise.enabled) return;
    onChange(String(franchise.id));
    handleClose();
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "Escape" && isOpen) {
      event.preventDefault();
      handleClose();
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleToggleOpen();
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        id="create-agent-franchise-select"
        disabled={disabled}
        onClick={handleToggleOpen}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-required={required}
        aria-label={ariaLabel}
        aria-busy={isLoading || isFetchingNextPage}
        className="custom-select flex w-full min-w-0 items-center rounded-md border border-gray-600 bg-gray-800 py-2 pr-10 pl-3 text-left text-white focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span
          className={`min-w-0 flex-1 truncate ${
            value && displayLabel ? "text-white" : "text-gray-400"
          }`}
        >
          {isLoading && !franchises.length
            ? placeholderLoading
            : displayLabel || placeholder}
        </span>
        {isFetchingNextPage ? (
          <span className="pointer-events-none mr-2 shrink-0" aria-hidden>
            <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
          </span>
        ) : null}
      </button>

      {isOpen ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          className="absolute top-full right-0 left-0 z-50 mt-1 max-h-48 overflow-hidden rounded-md border border-gray-600 bg-gray-800 shadow-lg"
        >
          <div
            ref={listRef}
            className="max-h-48 overflow-y-auto py-1"
            onScroll={handleScroll}
          >
            {isLoading && franchises.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-gray-400">
                <span
                  className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent"
                  aria-hidden
                />
                {placeholderLoading}
              </div>
            ) : (
              franchises.map((franchise) => {
                const isSelected = String(franchise.id) === String(value);
                const itemDisabled = !franchise.enabled;
                return (
                  <div key={franchise.id} role="presentation">
                    {itemDisabled ? (
                      <div
                        className="cursor-not-allowed px-3 py-2 text-sm text-gray-500"
                        aria-disabled="true"
                      >
                        {franchise.name} {disabledSuffix}
                      </div>
                    ) : (
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelectFranchise(franchise)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-white transition-colors hover:bg-gray-700 ${
                          isSelected ? "bg-gray-700" : ""
                        }`}
                      >
                        {isSelected ? (
                          <Icon
                            name="Check"
                            className="h-4 w-4 shrink-0 text-blue-400"
                            aria-hidden
                          />
                        ) : (
                          <span className="w-4 shrink-0" aria-hidden />
                        )}
                        <span>{franchise.name}</span>
                      </button>
                    )}
                  </div>
                );
              })
            )}
            {isFetchingNextPage ? (
              <div
                className="flex items-center justify-center gap-2 px-3 py-2"
                aria-live="polite"
              >
                <span
                  className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-500 border-t-transparent"
                  aria-hidden
                />
                <span className="text-xs text-gray-400">
                  {moreBrandsLoadingText}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FranchiseSelect;
